import { SelectChangeEvent } from '@mui/material';
import { TranscoderProfile, TranscoderVideoProfile } from '@vxui/common';
import { EBitrateBitsMultiple, convertBitrates } from '@vxui/components';
import { TableColumn } from 'react-data-table-component';
import { resolutionItems } from '../../../../models';
import { unsignedInteger } from '../../../../utils';
import { positiveNumber } from '../../../../utils/rules/positiveNumber';
import { Profiles, Resolutions, VideoCodecs } from '../../forms/profileForm.helpers';
import { CellInput, CellSelectBox, ColumnName, editRow } from '../profilesTable.helpers';
import { VideoTableColumnsProps } from '../profilesTable.types';

export const getColumns = ({
  t,
  actions,
  onChangeHandler,
  onResolutionChangeHandler,
  onCodecChangeHandler,
  onSelectChangeHandler,
  editableRows,
}: VideoTableColumnsProps): TableColumn<TranscoderVideoProfile>[] => {
  let editableRow: TranscoderVideoProfile | undefined;
  return [
    {
      name: ColumnName(t, 'name'),
      selector: row => row.name,
      cell: row => {
        editableRow = editRow(row, editableRows);
        return editableRow ? (
          <CellInput
            name="name"
            defaultValue={editableRow.name ?? ''}
            error={!!editableRow.errorMsg?.name}
            helperText={editableRow.errorMsg?.name}
            placeholder={t('transcoder.forms.profile.video.fields.name.placeholder')}
            row={editableRow}
            onChangeCellHandler={onChangeHandler}
          />
        ) : (
          row.name
        );
      },
      sortable: true,
      minWidth: '250px',
    },
    {
      name: ColumnName(t, 'codec'),
      selector: row => row.codec,
      cell: row =>
        editableRow ? (
          <CellSelectBox
            fieldName="codec"
            row={editableRow}
            value={editableRow?.codec?.toString() ?? ''}
            items={VideoCodecs}
            placeholder={t('transcoder.forms.profile.video.fields.codec.placeholder')}
            onChangeHandler={onCodecChangeHandler}
          />
        ) : (
          row.codec
        ),
      sortable: true,
      minWidth: '150px',
    },
    // hide fps until will be avaialable in backend
    // {
    //   name: ColumnName(t, 'fps'),
    //   selector: row => row.fps,
    //   cell: row =>
    //     editableRow ? (
    //       <CellSelectBox
    //         fieldName="fps"
    //         row={editableRow}
    //         value={editableRow?.fps?.toString() ?? ''}
    //         items={FpsOptions}
    //         placeholder={t('transcoder.forms.profile.video.fields.fps.placeholder')}
    //         onChangeHandler={(event: SelectChangeEvent<string>, row: Profile) =>
    //           onSelectChangeHandler({ event, row, field: 'fps' })
    //         }
    //       />
    //     ) : (
    //       row.fps
    //     ),
    //   sortable: true,
    //   minWidth: '50px',
    // },
    {
      name: ColumnName(t, 'resolution'),
      selector: row => row.width,
      cell: row =>
        editableRow ? (
          <CellSelectBox
            fieldName="resolution"
            row={editableRow}
            value={editableRow?.resolution?.toString() ?? ''}
            items={Resolutions}
            placeholder={t('transcoder.forms.profile.video.fields.resolution.placeholder')}
            onChangeHandler={onResolutionChangeHandler}
          />
        ) : (
          resolutionItems.find(channel => channel.value === row.resolution)?.label ?? row.resolution ?? ''
        ),
      sortable: true,
      width: '170px',
    },
    {
      name: ColumnName(t, 'bitrate', 'Mbps'),
      selector: row => row.bitrate,
      cell: row =>
        editableRow ? (
          <CellInput
            name="bitrate"
            defaultValue={editableRow.bitrate ?? ''}
            error={!!editableRow.errorMsg?.bitrate}
            helperText={editableRow.errorMsg?.bitrate}
            placeholder={t('transcoder.forms.profile.video.fields.bitrate.placeholder')}
            row={editableRow}
            onChangeCellHandler={onChangeHandler}
            {...positiveNumber()}
          />
        ) : (
          convertBitrates(row.bitrate, EBitrateBitsMultiple.megabitPerSecond, 'divide')
        ),
      sortable: true,
    },
    {
      name: ColumnName(t, 'profile'),
      selector: row => row.profile,
      cell: row =>
        editableRow ? (
          <CellSelectBox
            fieldName="profile"
            row={editableRow}
            value={editableRow?.profile?.toString() ?? ''}
            items={Profiles(row.codec)}
            placeholder={t('transcoder.forms.profile.video.fields.profile.placeholder')}
            onChangeHandler={(event: SelectChangeEvent<string>, row: TranscoderProfile) =>
              onSelectChangeHandler({ event, row, field: 'profile' })
            }
          />
        ) : (
          row.profile
        ),
      sortable: true,
    },
    {
      name: ColumnName(t, 'gopsize'),
      selector: row => row.gopSize,
      cell: row =>
        editableRow ? (
          <CellInput
            name="gopSize"
            defaultValue={editableRow.gopSize ?? ''}
            error={!!editableRow.errorMsg?.gopSize}
            helperText={editableRow.errorMsg?.gopSize}
            placeholder={t('transcoder.forms.profile.video.fields.gopsize.placeholder')}
            row={editableRow}
            onChangeCellHandler={onChangeHandler}
            {...unsignedInteger()}
          />
        ) : (
          row.gopSize
        ),
    },
    {
      name: ColumnName(t, 'usage'),
      selector: row => row.templates?.length || 0,
      cell: row => row.templates?.length || 0,
      sortable: true,
      maxWidth: '80px',
    },
    ...actions,
  ];
};
